import React from "react";
import { BasketMenuIcon } from "tsi-common-react/src/apps/checkout/BasketMenuIcon/index";
import { BasketLineVariant } from "tsi-common-react/src/apps/checkout/constants";
import { Image } from "tsi-common-react/src/components/Image/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { Messages } from "tsi-common-react/src/components/Message/index";
import { ValueProps } from "tsi-common-react/src/components/RichNavigationLinkCollection/index";
import { RichText } from "tsi-common-react/src/components/RichText/index";
import { SVGIcon } from "tsi-common-react/src/components/SVGIcon/index";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { isPageInsideIFrame as isPageInsideIFrameCheck } from "tsi-common-react/src/utils/settings";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { usePageURL, useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Context, reverse, templates } from "@reactivated";

import { IMenuItem } from "../../utils/menu.interface";
import { MainMenu } from "../MainMenu";

interface ValueProps {
    title: string;
    icon: string;
}

// The header props come from the global `chrome.site_header` context (promo
// settings + top nav menu). Flash messages arrive separately as the per-page
// PageChrome `messages`, fed in by PageSkeleton; `messages` is optional so
// callers that omit it still type-check, and at runtime PageSkeleton always
// passes it. The shared Messages widget renders each item's `.message`
// (auto-escaped, no HTML), so map the PageChrome MessageItem `.text` onto it;
// the widget derives its own category label client-side, so `category_label` /
// `is_html` are unused here (pillows rendering is unchanged).
type IMessages = templates.UtilityPageTemplate["page_chrome"]["messages"];

type IProps = React.ContextType<typeof Context>["chrome"]["site_header"] & {
    messages?: IMessages;
};

export const SiteHeader = (props: IProps) => {
    const isSSR = useIsSSR();
    const staticURL = useStaticURL();
    const { promo_settings, messages, top_nav_menu } = props;
    const messageItems = (messages ?? []).map((message) => ({
        level: message.level,
        message: message.text,
        tags: message.tags,
        level_tag: message.level_tag,
        extra_tags: message.extra_tags,
    }));

    const valueProps: ValueProps[] = [
        {
            title: t`Free Shipping`,
            icon: `${staticURL}img/pillows/top-val-item-2.png`,
        },
        {
            title: t`Proprietary TEMPUR Material`,
            icon: `${staticURL}img/pillows/top-val-item-1.png`,
        },
        {
            title: t`Assembled in USA`,
            icon: `${staticURL}img/pillows/top-val-item-4.png`,
        },
        {
            title: t`Easy Care`,
            icon: `${staticURL}img/pillows/top-val-item-5.png`,
        },
        {
            title: t`5-Year Warranty`,
            icon: `${staticURL}img/pillows/top-val-item-3.png`,
        },
    ];

    const homeURL = usePageURL("home");
    const basketURL = isoWebPageURL.wrap(reverse("basket:summary"));
    const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
    const customerProfileURL = isoWebPageURL.wrap(
        reverse("customer:profile-view"),
    );
    const isPageInsideIFrame = isSSR ? false : isPageInsideIFrameCheck();

    // Map top_nav_menu data to IMenuItem[]
    const menuItems: IMenuItem[] =
        top_nav_menu?.menu?.value && Array.isArray(top_nav_menu.menu.value)
            ? (top_nav_menu.menu.value as IMenuItem[])
            : [];

    return (
        <header className="l-full-width">
            <nav className="site-nav">
                {/* TODO(i18n): Wrap accessibility text in t tagged template: {t`Skip to main content`} */}
                <a className="screen-reader-skip" href="#main-content">
                    Skip to main content
                </a>
                <div className="site-nav__content l-capped-width--pillows">
                    <div className="site-nav__content-left">
                        <Link className="site-nav__logo-link" href={homeURL}>
                            {/* TODO(i18n): Wrap alt text in t tagged template: alt={t`TEMPUR-PILLOWS FROM TEMPUR-PEDIC®`} */}
                            <SVGIcon
                                svgClassName="site-nav__pillows-logo"
                                name="pillowsLogo"
                                alt="TEMPUR-PILLOWS FROM TEMPUR-PEDIC®"
                            />
                        </Link>
                        <Link
                            className="site-nav__logo-link site-nav__logo-link--mobile"
                            href={homeURL}
                        >
                            {/* TODO(i18n): Wrap alt text in t tagged template: alt={t`TEMPUR-PILLOWS FROM TEMPUR-PEDIC®`} */}
                            <SVGIcon
                                svgClassName="site-nav__pillows-logo site-nav__logo-mobile"
                                name="pillowsLogo"
                                alt="TEMPUR-PILLOWS FROM TEMPUR-PEDIC®"
                            />
                        </Link>
                    </div>

                    <div className="site-nav__main-menu">
                        <MainMenu cmsData={menuItems} />
                    </div>

                    <div className="site-nav__content-right">
                        <ul className="site-nav__main-menu site-nav__main-menu--pillows">
                            <li>
                                <span className="site-nav__main-menu-item site-nav__main-menu-item--mobile-icon">
                                    {/* TODO(i18n): Wrap alt text in t tagged template: alt={t`Header Divider`} */}
                                    <Image
                                        src={`${staticURL}img/pillows/top-nav-div.png`}
                                        className="site-nav__icon-div"
                                        aria-hidden="true"
                                        alt="Header Divider"
                                    />
                                </span>
                            </li>
                            <li>
                                <Link
                                    className="site-nav__main-menu-item site-nav__main-menu-item--mobile-icon"
                                    href={customerProfileURL}
                                >
                                    {/* TODO(i18n): Wrap alt text in t tagged template: alt={t`Account`} */}
                                    <Image
                                        src={`${staticURL}img/pillows/top-nav-account.png`}
                                        className="site-nav__icon-account"
                                        alt="Account"
                                    />
                                </Link>
                            </li>
                            <li className="site-nav__secondary-menu__item site-nav__secondary-menu__item--cart">
                                <BasketMenuIcon
                                    basketURL={basketURL}
                                    checkoutURL={checkoutURL}
                                    isPageInsideIFrame={isPageInsideIFrame}
                                    basketLineVariant={
                                        BasketLineVariant.MINIMAL_ENHANCED
                                    }
                                    isSSR={isSSR}
                                />
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            <div className="header-notification">
                <div className="l-capped-width">
                    <div className="header-notification__copy">
                        {promo_settings.promo_header?.value.map((block) => (
                            <React.Fragment key={block.id}>
                                {block.value.copy && (
                                    <RichText
                                        className="header-notification__rich-text"
                                        html={block.value.copy}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>

            {messageItems && <Messages messages={messageItems} />}

            <div className="header-value-prop-bar">
                <div className="l-capped-width">
                    <ul className="header-value-prop-list pdp-header-value-prop-bar__list">
                        {valueProps.map((item, index) => (
                            <li
                                key={index}
                                className="header-value-prop-list-item"
                            >
                                <div className="header-value-prop-list-item__icon">
                                    <div
                                        className="header-value-prop-list-icon"
                                        style={{
                                            backgroundImage: `url(${item.icon})`,
                                        }}
                                    ></div>
                                </div>
                                <div className="header-value-prop-list-item__copy">
                                    <div className="header-value-prop-list-item__title">
                                        {item.title}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </header>
    );
};
